import React, { useEffect, useState } from "react";
import { Typography, Grid, Modal, Button, Box, Divider, useTheme, TextField, IconButton } from "@mui/material";
import moment from 'moment';
import CustomTable from "../../../shared/CustomTable";
import organizationServices from "../../../services/organization-service";
import cityServices from "../../../services/city-service";
import { constants } from "../../../constants/app.constants";
import { Close, History, RemoveRedEyeOutlined, SpeakerNotesOutlined } from "@mui/icons-material";
import Receipt from "./receipt";
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 2,
};

export default function ParkingsView(props) {
  const theme = useTheme();
  const user = JSON.parse(sessionStorage.getItem('userLogged'));
  const [organizations, setOrganizations] = useState([]);
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receipt, setReceipt] = useState({});
  const [externalLogModal, setExternalLogModal] = useState(false);
  const [selectedExternalLog, setSelectedExternalLog] = useState(null);
  
  const columns = [
    { field: 'parking_id', headerName: props.literals.parking_id, type: 'number', minWidth: 150, filterable: true },
    {
      field: 'org', headerName: 'Organization', valueFormatter: (params) => params?.value?.org_name, minWidth: 150, type: "singleSelect",
      valueOptions: organizations, filterable: true
    },
    {
      field: 'zone', headerName: 'Zone', valueFormatter: (params) => params?.value?.zone_name, minWidth: 200, type: "singleSelect",
      valueOptions: zones, filterable: true
    },
    {
      field: 'city', headerName: 'City', valueFormatter: (params) => params.value?.city_name, minWidth: 150, type: "singleSelect",
      valueOptions: cities, filterable: true
    },
    { field: 'ticket', headerName: 'Moneris Token', minWidth: 400, filterable: true },
    // { field: 'user', headerName: 'User Name', valueFormatter: (params) => params.value?.fname || params.row?.user?.email, minWidth: 150, filterable: false },
    { field: 'email', headerName: 'User Email', valueGetter: (params) => params.row?.user?.email, minWidth: 200, filterable: false },
    { field: 'plate', headerName: 'Plate', minWidth: 150, filterable: true },
    { field: 'amount', headerName: 'Amount', type: 'number', valueFormatter: (params) => '$ ' + (params.value / 100).toFixed(2), minWidth: 150, filterable: true },
    { field: 'refund_amount', headerName: 'Refund Amount', type: 'number', valueFormatter: (params) => params.value ? '$ ' + (params.value / 100).toFixed(2) : '--', minWidth: 150, filterable: true },
    { field: 'transaction_date', headerName: 'Transaction Date', type: 'date', valueFormatter: (params) => params?.value ? moment(params?.value).format('ll hh:mm a') : '--', minWidth: 200, filterable: true },
    { field: 'from', headerName: 'Start Date/Time', type: 'date', valueFormatter: (params) => moment(params?.value).format('ll hh:mm a'), minWidth: 200, filterable: true },
    { field: 'to', headerName: 'End Date/Time', type: 'date', valueFormatter: (params) => moment(params?.value).format('ll hh:mm a'), minWidth: 200, filterable: true },
    {
      field: 'edit_plate',
      headerName: props.literals.edit_plate,
      width: 150,
      renderCell: (params) => (
        <>
          {params.row.zone?.no_of_times_plate_can_edit > params.row.no_of_times_plate_edited && (moment() <= moment(params.row.to)) &&
            <Button 
              variant="outlined"
              color="primary"
              size="small"
              onClick={()=>props.openEditPlateModal(params.row)}
            >
              {props.literals.edit_plate}
            </Button>
          }
        </>
      )
    },
    {
      field: 'status',
      headerName: props.literals.parking_status,
      minWidth: 200,
      renderCell: (params) => (
        <>
          {params.row.is_permit && (moment() <= moment(params.row.to)) &&
            <Button 
              variant="outlined"
              color="primary"
              size="small"
              onClick={()=>props.openNoteModal(params.row)}
            >
              {props.literals.cancel_permit}
            </Button>
          }
          {params.row.is_permit && params.row.status == 'cancelled' &&
            <Typography variant="caption" sx={{color: '#af0000', background: '#af00004f', px: 2, borderRadius: '10px'}}>
              {props.literals.cancelled}
            </Typography>
          }
        </>
      )
    },
    {
      field: 'action',
      headerName: props.literals.receipt,
      modal: true
    },
    {
      field: 'transaction_history',
      headerName: props.literals.transaction_history,
      width: 150,
      renderCell: (params) => (
        <>
          {params.row.is_permit && <Button
            type="button"
            onClick={() => { props.onTransactionHistory(params?.row) }}
          >
            <History />
          </Button>}
        </>
      )
    },
    {
      field: 'external_request',
      headerName: props.literals.external_request,
      width: 200,
      renderCell: (params) => (
        <>
          <Button
            type="button"
            onClick={() => {
              setExternalLogModal(true);
              setSelectedExternalLog(
                !params?.row?.external_request ? 'external_not_found' :
                params?.row?.external_request == 'external_not_configured' ? 
                'external_not_configured' : 
                parseEscapedJsonString(params?.row?.external_request)
              )}}
          >
            <SpeakerNotesOutlined />
          </Button>
        </>
      )
    },
  ]

  const parseEscapedJsonString = (escapedString) => {
    const outer = JSON.parse(escapedString);
    const cleanedPayloadStr = outer.payload.replace(/^jsonIn=/, "");
    const innerPayload = JSON.parse(cleanedPayloadStr);
    return {
      payload: innerPayload,
      response: outer.response
    };
  }

  useEffect(() => {
    getOrganizations();
    getCities();
    getZones();
  }, [])

  const getOrganizations = async () => {
    props.setSpinner(true);
    const res = await organizationServices.getOrganizations();
    let org;
    if (user.result?.role !== 'root') {
      org = res.data.find(x => x._id == props.org_id);
    } else {
      org = res.data;
    }
    setOrganizations(org.map(x => ({ value: x._id, label: x.org_name })));
    props.setSpinner(false);
  }

  const getCities = async () => {
    props.setSpinner(true);
    const res = await cityServices.getCities({ org_id: props.org_id });
    setCities(res.data.map(x => ({ value: x._id, label: x.city_name })))
    props.setSpinner(false);
  }

  const getZones = async () => {
    props.setSpinner(true);
    const res = await cityServices.getZones({ org_id: props.org_id });
    setZones(res.data.map(x => ({ value: x._id, label: x.zone_name })))
    props.setSpinner(false);
  }

  const onModal = (item) => {
    setShowReceipt(true);
    setReceipt(item);
  }
  return (
    <Grid container spacing={3} sx={{ placeContent: "center" }}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" color="primary">{props.literals.parkings}</Typography>
      </Grid>
      <Grid item xs={12}>
        <CustomTable
          columns={columns}
          data={props.parking}

          onPageChange={props.onPageChange}
          onFilter={props.onFilter}
          onSort={props.onSort}
          onEdit={props.onEdit}
          delItem={props.delItem}
          modal={onModal}
        />
      </Grid>
      <Modal
        open={showReceipt}
        onClose={() => setShowReceipt(false)}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          bgcolor: 'background.paper',
          boxShadow: 24
        }}>
          <Receipt
            literals={props.literals}
            receipt={receipt}
            onClose={() => setShowReceipt(false)}
          />
        </Box>
      </Modal>
      <Modal
        open={props.showHistoryModal}
        onClose={props.closeHistoryModal}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2
        }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="primary">{props.literals.transaction_history}</Typography>
            </Grid>
            <Grid item xs={6} textAlign={'end'}>
              <Button
                type="button"
                onClick={props.closeHistoryModal}
              >
                <Close />
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{maxHeight: '80vh', overflow: 'auto'}}>
            {props.transactionHistory?.map((y, i) => {
              return (
                <>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='caption' align='left'>
                      {props.literals.transaction_date}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='subtitle2' align='left' sx={{ fontWeight: 'bold' }} >
                      {moment(y.transaction_date).format("MMMM Do YYYY, hh:mm a")}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='caption' align='left'>
                      {props.literals.amount}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='subtitle2' align='left' sx={{ fontWeight: 'bold' }} >
                      CA$ {(y.amount / 100).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='caption' align='left'>
                      {props.literals.permit_start_date}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='subtitle2' align='left' sx={{ fontWeight: 'bold' }} >
                      {moment(y.parking_from).format("MMMM Do YYYY, hh:mm a")}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='caption' align='left'>
                      {props.literals.permit_end_date}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='subtitle2' align='left' sx={{ fontWeight: 'bold' }} >
                      {moment(y.parking_to).format("MMMM Do YYYY, hh:mm a")}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='caption' align='left'>
                      {props.literals.allow_auto_renew}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='subtitle2' align='left' sx={{ fontWeight: 'bold' }} >
                      {y.allow_auto_renew ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  {y.allow_auto_renew && <><Grid item md={3} sm={6} xs={6}>
                    <Typography variant='caption' align='left'>
                      {props.literals.next_renew_at}
                    </Typography>
                  </Grid>
                    <Grid item md={3} sm={6} xs={6}>
                      <Typography variant='subtitle2' align='left' sx={{ fontWeight: 'bold' }} >
                        {moment(y.next_renew_at).format("MMMM Do YYYY, hh:mm a")}
                      </Typography>
                    </Grid></>}
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='caption' align='left'>
                      {props.literals.payment_status}
                    </Typography>
                  </Grid>
                  <Grid item md={3} sm={6} xs={6}>
                    <Typography variant='subtitle2' align='left' sx={{ fontWeight: 'bold' }} >
                      {y.payment_status}
                    </Typography>
                  </Grid>
                  {y.payment_status !== 'succeeded' && <>
                    <Grid item md={3} sm={6} xs={6}>
                      <Typography variant='caption' align='left'>
                        {props.literals.failure_reason}
                      </Typography>
                    </Grid>
                    <Grid item md={3} sm={6} xs={6}>
                      <Typography variant='subtitle2' align='left' sx={{ fontWeight: 'bold' }} >
                        {y.failure_reason}
                      </Typography>
                    </Grid>
                  </>}
                  {props.transactionHistory.length !== (i + 1) && <Grid item xs={12}>
                    <Divider sx={{ background: theme.palette.primary.main }} />
                  </Grid>}
                </>
              )
            })}
          </Grid>

        </Box>
      </Modal>
      <Modal
        open={props.editPlateModal}
        onClose={props.closeEditPlateModal}
      >
        <Box component="form" onSubmit={props.handleEditPlate} sx={style}>
          <Grid container spacing={3} sx={{ placeContent: "center" }}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="primary" className="font-bold m-2 font-gray">
                {props.literals.edit_plate}
              </Typography>
            </Grid>
            <Grid item xs={6} align='right'>
              <IconButton color="primary" component="label" onClick={props.closeEditPlateModal}>
                <Close />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={props.literals.plate}
                color="primary"
                name="plate"
                value={props.inputField.plate}
                onChange={(e) => {
                  if (e.target.value !== "" && !constants.regexPatterns.alphaNumeric.test(e.target.value) ) {
                    return;
                  }
                  props.handleChange(e);
                }}
                size="small"
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} align='right'>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                size="small"
              >
                {props.literals.submit}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Modal
        open={props.noteModal}
        onClose={props.closeNoteModal}
      >
        <Box component="form" onSubmit={props.handleCancelPermit} sx={style}>
          <Grid container spacing={3} sx={{ placeContent: "center" }}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="primary" className="font-bold m-2 font-gray">
                {props.literals.cancel_reason}
              </Typography>
            </Grid>
            <Grid item xs={6} align='right'>
              <IconButton color="primary" component="label" onClick={props.closeNoteModal}>
                <Close />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={props.literals.notes}
                color="primary"
                name="note"
                value={props.inputField.note}
                onChange={(e) => {
                  props.handleChange(e);
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={props.literals.refund_amount_in_cents}
                color="primary"
                name="refund_amount"
                type="number"
                InputProps={{
                  inputProps: { 
                      max: props.selectedParking?.amount, min: 0 
                  }
                }}
                helperText={props.literals.zero_means_no_refund}
                value={props.inputField.refund_amount}
                onChange={(e) => {
                  props.handleChange(e);
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} align='right'>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                size="small"
              >
                {props.literals.submit}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Modal
        open={externalLogModal}
        onClose={()=> setExternalLogModal(false)}
      >
        <Box component="form" onSubmit={props.handleCancelPermit} sx={style}>
          <Grid container spacing={3} sx={{ placeContent: "center" }}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="primary" className="font-bold m-2 font-gray">
                {props.literals.external_request}
              </Typography>
            </Grid>
            <Grid item xs={6} align='right'>
              <IconButton color="primary" component="label" onClick={()=> setExternalLogModal(false)}>
                <Close />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              <JSONPretty data={selectedExternalLog}></JSONPretty>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Grid>
  );
}
